import { type MouseEvent as ReactMouseEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Building2, CalendarClock, ClipboardList, Coins, Download, FileText, Forklift, Hash, Mail, MapPin, Pencil, Percent, Phone, Plus, Receipt, Search, Trash2, TrendingUp, UserPlus, UserRound, Users, UserX } from 'lucide-react';
import type { RecordDeletionGateway, TenantSummary } from '@forq/contracts';
import type {
  CustomerAddressRecord,
  CustomerContactRecord,
  CustomerDetailRecord,
  CustomerFinancials,
  CustomerListRecord,
  CustomerOperation,
  CustomerPage,
  CustomerStatus,
  CustomersGateway,
} from '@forq/customers-data';
import type { MachineListRecord, MachinesGateway } from '@forq/machines-data';
import { emptyCompanyProfile, type CompanyProfileGateway, type TenantCompanyProfile } from '@forq/platform-data';
import { CustomizableMetrics, describeDeleteOutcome, EmptyState, RecordDeleteButton, SearchSelect, useContextMenu, useRecordDeleteFlow, type ContextMenuItem } from '@forq/ui';
import { AddressDialog, AgreementsDialog, ContactDialog, CustomerDialog } from './CustomerDialogs';
import { CustomerDocumentsSection } from './CustomerDocumentsSection';
import { renderCustomerSheetHtml } from './documents';

interface CustomersPageProps {
  readonly tenant: TenantSummary;
  readonly permissions: ReadonlySet<string>;
  readonly enabledFeatureIds: readonly string[];
  readonly gateway: CustomersGateway;
  readonly machinesGateway: MachinesGateway;
  readonly deletionGateway: RecordDeletionGateway;
  readonly companyGateway: CompanyProfileGateway;
  /** Verknüpfte Vorgänge (Auftrag/Service/Miete/Angebot) je Kundennummer -- für den Vorgänge-Tab. */
  readonly loadOperations?: (tenantId: string) => Promise<Readonly<Record<string, readonly CustomerOperation[]>>>;
  /** Kennzahlen (Umsatz, offene Posten, Rechnungen) je Kundennummer -- für Übersicht & Rechnungen. */
  readonly loadFinancials?: (tenantId: string) => Promise<Readonly<Record<string, CustomerFinancials>>>;
  readonly mode: 'demo' | 'supabase';
}

const machineStatusLabel: Record<string, string> = {
  customer: 'Kundenmaschine', stock: 'Bestand', rental: 'Mietpark', sold: 'Verkauft', 'out-of-service': 'Außer Betrieb',
};

const operationKindLabel: Record<CustomerOperation['kind'], string> = {
  order: 'Auftrag', service: 'Service', rental: 'Miete', offer: 'Angebot',
};

const paymentStatusLabel: Record<string, string> = {
  open: 'Offen', partial: 'Teilbezahlt', paid: 'Bezahlt', not_relevant: '—',
};

const detailTabs: readonly { readonly id: string; readonly label: string }[] = [
  { id: 'overview', label: 'Übersicht' },
  { id: 'vorgaenge', label: 'Vorgänge' },
  { id: 'rechnungen', label: 'Rechnungen & OPOS' },
  { id: 'konditionen', label: 'Konditionen' },
  { id: 'adressen', label: 'Anschriften & Kontakte' },
  { id: 'bemerkungen', label: 'Bemerkungen' },
  { id: 'dokumente', label: 'Dokumente' },
  { id: 'historie', label: 'Historie' },
];

const emptyPage: CustomerPage = { items: [], total: 0, page: 1, pageSize: 25 };

function messageOf(error: unknown): string {
  return error instanceof Error && error.message ? error.message : 'Kundendaten konnten nicht geladen werden.';
}
function euro(value: number): string { return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(value); }
function dateShort(value: string): string { return value ? new Date(value).toLocaleDateString('de-DE') : '–'; }
function csvCell(value: string | number | null): string {
  const text = String(value ?? '');
  return /[";\r\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}

function statusLabel(status: CustomerStatus): string {
  if (status === 'blocked') return 'Gesperrt';
  if (status === 'prospect') return 'Interessent';
  return 'Aktiv';
}

function addressKindLabel(kind: CustomerAddressRecord['kind']): string {
  if (kind === 'delivery') return 'Lieferanschrift';
  if (kind === 'site') return 'Einsatzort';
  return 'Rechnungsanschrift';
}

export function CustomersPage({ tenant, permissions, enabledFeatureIds, gateway, machinesGateway,
  deletionGateway, companyGateway, loadOperations, loadFinancials, mode }: CustomersPageProps) {
  const navigate = useNavigate();
  const { kundennummer } = useParams();
  const [query, setQuery] = useState('');
  const [status, setStatus] = useState<CustomerStatus | 'all'>('all');
  const [bestandFrom, setBestandFrom] = useState('');
  const [bestandTo, setBestandTo] = useState('');
  const [page, setPage] = useState<CustomerPage>(emptyPage);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [detail, setDetail] = useState<CustomerDetailRecord | null>(null);
  const [assignedMachines, setAssignedMachines] = useState<readonly MachineListRecord[]>([]);
  const [financialsMap, setFinancialsMap] = useState<Readonly<Record<string, CustomerFinancials>>>({});
  const [operationsMap, setOperationsMap] = useState<Readonly<Record<string, readonly CustomerOperation[]>>>({});
  const [company, setCompany] = useState<TenantCompanyProfile>(emptyCompanyProfile);
  const [tab, setTab] = useState('overview');
  const [viewMode, setViewMode] = useState<'split' | 'table'>('split');
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [mutationBusy, setMutationBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** Erfolgsmeldung nach dem Löschen -- getrennt vom Fehlerzustand, es ist ja keiner. */
  const [notice, setNotice] = useState<string | null>(null);
  const [customerDialog, setCustomerDialog] = useState<'create' | 'edit' | null>(null);
  const [addressDialog, setAddressDialog] = useState<CustomerAddressRecord | 'new' | null>(null);
  const [contactDialog, setContactDialog] = useState<CustomerContactRecord | 'new' | null>(null);
  const [agreementsDialog, setAgreementsDialog] = useState(false);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [notesEditing, setNotesEditing] = useState(false);
  const [notesDraft, setNotesDraft] = useState('');
  const [notesSaving, setNotesSaving] = useState(false);

  const canCreate = permissions.has('customers.create') && enabledFeatureIds.includes('customers.create');
  const canEdit = permissions.has('customers.edit') && enabledFeatureIds.includes('customers.edit');

  // Vom Startbild aus (?neu) direkt den Anlegen-Dialog öffnen -- ein Klick weniger.
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    if (searchParams.get('neu') === null || !canCreate) return;
    setCustomerDialog('create');
    setSearchParams((prev) => { prev.delete('neu'); return prev; }, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const canManageAddresses = permissions.has('customers.edit') && enabledFeatureIds.includes('customers.addresses');
  const canManageContacts = permissions.has('customers.contacts.manage') && enabledFeatureIds.includes('customers.contacts');

  // FORQ-eigenes Rechtsklick-Menü auf Kunden-Zeilen (statt des Browser-Menüs). Bearbeiten/Kundenblatt
  // brauchen den Detaildatensatz -- also erst laden, dann öffnen (wie im Maschinenstamm).
  const openFromList = useCallback((customer: CustomerListRecord, then: 'edit' | 'sheet') => {
    void gateway.getCustomer(tenant.id, customer.id)
      .then((full) => { setDetail(full); setSelectedId(full.id); if (then === 'edit') setCustomerDialog('edit'); else setSheetOpen(true); })
      .catch((loadError: unknown) => setError(messageOf(loadError)));
  }, [gateway, tenant.id]);
  const canDelete = permissions.has('customers.delete');
  const { open: openCustomerMenu, menu: customerContextMenu } = useContextMenu();
  const { requestDelete: requestCustomerDelete, deleteDialog: customerDeleteDialog } = useRecordDeleteFlow(deletionGateway, tenant.id);
  const duplicateCheckEnabled = enabledFeatureIds.includes('customers.duplicates');

  const financials = detail ? (financialsMap[detail.number] ?? null) : null;
  const operations = detail ? (operationsMap[detail.number] ?? []) : [];

  const loadList = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await gateway.searchCustomers({ tenantId: tenant.id, query, status, bestandFrom, bestandTo, pageSize: 25 });
      setPage(result);
      setSelectedId((current) => current && result.items.some((item) => item.id === current)
        ? current
        : result.items[0]?.id ?? null);
    } catch (loadError) {
      setPage(emptyPage);
      setSelectedId(null);
      setError(messageOf(loadError));
    } finally {
      setLoading(false);
    }
  }, [gateway, query, status, bestandFrom, bestandTo, tenant.id]);

  // Menü-Einträge fürs Rechtsklick-Menü -- hier, weil loadList (fürs Neuladen nach dem Löschen)
  // erst oben deklariert sein muss.
  const customerMenuItems = useCallback((customer: CustomerListRecord): ContextMenuItem[] => [
    { label: 'Öffnen', icon: <UserRound size={15} />, onSelect: () => { setViewMode('split'); navigate(`/kunden/${customer.number}`); } },
    { label: 'Bearbeiten', icon: <Pencil size={15} />, disabled: !canEdit, onSelect: () => openFromList(customer, 'edit') },
    { label: 'Kundenblatt', icon: <FileText size={15} />, separatorBefore: true, onSelect: () => openFromList(customer, 'sheet') },
    {
      label: 'Löschen', icon: <Trash2 size={15} />, danger: true, disabled: !canDelete, separatorBefore: true,
      onSelect: () => requestCustomerDelete({
        entity: 'customer', id: customer.id, noun: 'Kunde', label: customer.displayName,
        onDone: (outcome) => {
          setNotice(describeDeleteOutcome(outcome, 'Kunde', customer.displayName));
          setSelectedId((current) => (current === customer.id ? null : current));
          setDetail((current) => (current?.id === customer.id ? null : current));
          void loadList();
        },
      }),
    },
  ], [navigate, canEdit, canDelete, openFromList, requestCustomerDelete, loadList]);

  const loadDetail = useCallback(async (customerId: string): Promise<void> => {
    setDetailLoading(true);
    try {
      setDetail(await gateway.getCustomer(tenant.id, customerId));
    } catch (loadError) {
      setError(messageOf(loadError));
    } finally {
      setDetailLoading(false);
    }
  }, [gateway, tenant.id]);

  useEffect(() => {
    const timer = window.setTimeout(() => void loadList(), 180);
    return () => window.clearTimeout(timer);
  }, [loadList]);

  useEffect(() => {
    if (!selectedId) {
      setDetail(null);
      return;
    }
    void loadDetail(selectedId);
  }, [loadDetail, selectedId]);

  // Beim Kundenwechsel Tab + Notiz-Editor zurücksetzen -- sonst bleibt man auf einem Tab hängen.
  useEffect(() => { setTab('overview'); setNotesEditing(false); }, [detail?.id]);

  // Kennzahlen + verknüpfte Vorgänge einmal je Mandant laden und über die Kundennummer indexieren.
  useEffect(() => {
    if (!loadFinancials) { setFinancialsMap({}); return undefined; }
    let active = true;
    loadFinancials(tenant.id).then((map) => { if (active) setFinancialsMap(map); }).catch(() => { if (active) setFinancialsMap({}); });
    return () => { active = false; };
  }, [loadFinancials, tenant.id]);

  useEffect(() => {
    if (!loadOperations) { setOperationsMap({}); return undefined; }
    let active = true;
    loadOperations(tenant.id).then((map) => { if (active) setOperationsMap(map); }).catch(() => { if (active) setOperationsMap({}); });
    return () => { active = false; };
  }, [loadOperations, tenant.id]);

  useEffect(() => {
    let active = true;
    companyGateway.loadProfile(tenant.id).then((profile) => { if (active) setCompany(profile); }).catch(() => {});
    return () => { active = false; };
  }, [companyGateway, tenant.id]);

  // Load the machines assigned to the selected customer (by business number).
  useEffect(() => {
    if (!detail) { setAssignedMachines([]); return undefined; }
    let cancelled = false;
    machinesGateway.searchMachines({ tenantId: tenant.id, customerNumber: detail.number, pageSize: 50 })
      .then((page) => { if (!cancelled) setAssignedMachines(page.items); })
      .catch(() => { if (!cancelled) setAssignedMachines([]); });
    return () => { cancelled = true; };
  }, [detail, machinesGateway, tenant.id]);

  // The customer number in the URL (/kunden/:kundennummer) opens that record — deep-linkable.
  useEffect(() => {
    if (!kundennummer) return;
    const found = page.items.find((item) => item.number === kundennummer);
    if (found) setSelectedId(found.id);
  }, [kundennummer, page.items]);

  function applySavedCustomer(saved: CustomerDetailRecord, created = false): void {
    setDetail(saved);
    setSelectedId(saved.id);
    navigate(`/kunden/${saved.number}`);
    setError(null);
    setCustomerDialog(null);
    setAddressDialog(null);
    setContactDialog(null);
    if (created) setQuery(saved.number);
    else void loadList();
  }

  async function deleteAddress(address: CustomerAddressRecord): Promise<void> {
    if (!detail || !window.confirm(`Anschrift „${address.label}“ wirklich löschen?`)) return;
    setMutationBusy(true);
    setError(null);
    try {
      applySavedCustomer(await gateway.deleteAddress(tenant.id, detail.id, address.id));
    } catch (mutationError) {
      setError(messageOf(mutationError));
    } finally {
      setMutationBusy(false);
    }
  }

  async function deactivateContact(contact: CustomerContactRecord): Promise<void> {
    if (!detail || !window.confirm(`${contact.firstName} ${contact.lastName} wirklich deaktivieren?`)) return;
    setMutationBusy(true);
    setError(null);
    try {
      applySavedCustomer(await gateway.updateContact({
        tenantId: tenant.id,
        customerId: detail.id,
        contactId: contact.id,
        salutation: contact.salutation,
        firstName: contact.firstName,
        lastName: contact.lastName,
        position: contact.position,
        email: contact.email,
        phone: contact.phone,
        mobile: contact.mobile,
        primary: false,
        active: false,
      }));
    } catch (mutationError) {
      setError(messageOf(mutationError));
    } finally {
      setMutationBusy(false);
    }
  }

  // Bemerkungen inline speichern. Wichtig: die volle Payload aus dem Datensatz mitschicken, sonst
  // leert customer_update die übrigen Felder. Die Konditionen (agreements) liegen in einer eigenen
  // Spalte und werden von customer_update NICHT angefasst -- Speichern der Notiz ist also unkritisch.
  async function saveNotes(): Promise<void> {
    if (!detail) return;
    setNotesSaving(true);
    setError(null);
    try {
      const saved = await gateway.updateCustomer({
        tenantId: tenant.id,
        customerId: detail.id,
        kind: detail.kind,
        companyName: detail.companyName,
        firstName: detail.firstName,
        lastName: detail.lastName,
        vatId: detail.vatId,
        taxNumber: detail.taxNumber,
        email: detail.email,
        phone: detail.phone,
        website: detail.website,
        notes: notesDraft,
        status: detail.status,
      });
      setDetail(saved);
      setNotesEditing(false);
    } catch (mutationError) {
      setError(messageOf(mutationError));
    } finally {
      setNotesSaving(false);
    }
  }

  // Alle (gefilterten) Kunden als CSV -- zieht die volle Trefferliste, nicht nur die Seite.
  const exportCsv = useCallback(async () => {
    try {
      const all = await gateway.searchCustomers({ tenantId: tenant.id, query, status, bestandFrom, bestandTo, pageSize: 1000 });
      const headers = ['Nummer', 'Name', 'Art', 'Status', 'Ort', 'Telefon', 'E-Mail', 'USt-ID', 'Ansprechpartner', 'Kunde seit'];
      const rows = all.items.map((customer) => [
        customer.number, customer.displayName, statusLabel(customer.status), customer.city,
        customer.phone, customer.email, customer.vatId, customer.contactCount,
        customer.activeSince ? new Date(customer.activeSince).toLocaleDateString('de-DE') : '',
      ]);
      const bom = String.fromCharCode(0xfeff);
      const csv = [headers, ...rows].map((row) => row.map(csvCell).join(';')).join('\r\n');
      const blob = new Blob([`${bom}${csv}`], { type: 'text/csv;charset=utf-8' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `kunden-${tenant.numberPrefix}.csv`;
      document.body.appendChild(link); link.click(); link.remove();
      URL.revokeObjectURL(link.href);
    } catch (exportError) {
      setError(messageOf(exportError));
    }
  }, [gateway, tenant.id, tenant.numberPrefix, query, status, bestandFrom, bestandTo]);

  if (!enabledFeatureIds.includes('customers.list')) {
    return <EmptyState eyebrow="Kundenmodul" title="Die Kundenliste ist nicht freigegeben." description="Die Funktion wird über Rolle und Feature-Konfiguration gesteuert." />;
  }

  return (
    <div className="customers-page">
      <header className="customers-header">
        <div>
          <span className="customers-eyebrow">FORQ Customers</span>
          <h1>Kunden & Ansprechpartner</h1>
          <p>{tenant.name} · Nummernbereich {tenant.numberPrefix}xxxx</p>
        </div>
        <div className="customers-header__actions">
          <span className={`customers-mode ${mode === 'demo' ? 'is-demo' : ''}`}>{mode === 'demo' ? 'Demo-Daten' : 'Supabase verbunden'}</span>
          <button type="button" className="customers-secondary" disabled={page.total === 0} onClick={() => void exportCsv()} title="Alle gefilterten Kunden als CSV exportieren"><Download size={16} /> Export</button>
          <button type="button" className="customers-primary" disabled={!canCreate} onClick={() => setCustomerDialog('create')}><Plus size={17} /> Kunde anlegen</button>
        </div>
      </header>

      {error ? <div className="customers-error" role="alert"><span>{error}</span><button type="button" onClick={() => void loadList()}>Neu laden</button></div> : null}
      {notice ? <div className="customers-notice" role="status"><span>{notice}</span><button type="button" onClick={() => setNotice(null)}>Schließen</button></div> : null}

      <section className="customers-toolbar">
        <label className="customers-search"><Search size={17} /><input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Name, Kundennummer, Ort, E-Mail oder USt-ID suchen …" /></label>
        <div className="customers-viewtoggle" role="group" aria-label="Ansicht">
          <button type="button" className={viewMode === 'split' ? 'is-active' : ''} onClick={() => setViewMode('split')}>Detail</button>
          <button type="button" className={viewMode === 'table' ? 'is-active' : ''} onClick={() => setViewMode('table')}>Liste</button>
        </div>
        <SearchSelect value={status} onChange={(next) => setStatus(next as CustomerStatus | 'all')} ariaLabel="Kundenstatus" options={[{ value: 'all', label: 'Alle Status' }, { value: 'active', label: 'Aktiv' }, { value: 'prospect', label: 'Interessent' }, { value: 'blocked', label: 'Gesperrt' }]} />
        <div className="customers-bestand" title="Bestand im Zeitraum">
          <span>Bestand</span>
          <input type="date" aria-label="Bestand von" value={bestandFrom} onChange={(event) => setBestandFrom(event.target.value)} />
          <span>–</span>
          <input type="date" aria-label="Bestand bis" value={bestandTo} onChange={(event) => setBestandTo(event.target.value)} />
          {(bestandFrom || bestandTo) ? <button type="button" className="customers-bestand__clear" onClick={() => { setBestandFrom(''); setBestandTo(''); }}>×</button> : null}
        </div>
        <span>{loading ? 'Suche läuft …' : `${page.total} Treffer`}</span>
      </section>

      {viewMode === 'split' ? (
      <div className="customers-workspace">
        <aside className="customers-list" aria-label="Kundenliste">
          {page.items.map((customer) => (
            <button type="button" key={customer.id} className={customer.id === selectedId ? 'is-active' : ''} onClick={() => navigate(`/kunden/${customer.number}`)} onContextMenu={(event) => openCustomerMenu(event, customerMenuItems(customer))}>
              <span className="customers-list__icon"><Building2 size={18} /></span>
              <span className="customers-list__body"><strong>{customer.displayName}</strong><small>{customer.number} · {customer.city || 'Ort fehlt'}</small></span>
              <span className={`customers-status is-${customer.status}`}>{statusLabel(customer.status)}</span>
            </button>
          ))}
          {!loading && page.items.length === 0 ? <p>Keine Kunden gefunden.</p> : null}
        </aside>

        <main className="customers-detail">
          {detailLoading ? <div className="customers-loading">Kundendaten werden geladen …</div> : null}
          {!detailLoading && !detail ? <EmptyState eyebrow="Kundenauswahl" title="Wähle einen Kunden aus." description="Links findest du alle Kunden des aktiven Mandanten." /> : null}
          {!detailLoading && detail ? (
            <>
              <div className="customers-detail__title">
                <div><span className="customers-eyebrow">Kunde {detail.number}</span><h2>{detail.displayName}</h2><p>{detail.kind === 'company' ? 'Unternehmen' : 'Privatkunde'} · {statusLabel(detail.status)} · Kunde seit {dateShort(detail.activeSince)}{detail.activeUntil ? ` · Abgang ${dateShort(detail.activeUntil)}` : ''}</p></div>
                <div className="customers-detail__actions">
                  <span className="customers-vat">USt-ID: {detail.vatId || 'nicht hinterlegt'}</span>
                  <button type="button" className="customers-secondary" onClick={() => setSheetOpen(true)}><FileText size={15} /> Kundenblatt</button>
                  <button type="button" className="customers-secondary" disabled={!canEdit} onClick={() => setCustomerDialog('edit')}><Pencil size={15} /> Bearbeiten</button>
                  <RecordDeleteButton className="customers-secondary is-danger" gateway={deletionGateway} tenantId={tenant.id} entity="customer" id={detail.id} noun="Kunde" label={detail.displayName} canDelete={permissions.has('customers.delete')} onDone={(outcome) => { setNotice(describeDeleteOutcome(outcome, 'Kunde', detail.displayName)); setSelectedId(null); setDetail(null); navigate('/kunden'); void loadList(); }} />
                </div>
              </div>

              <nav className="customers-tabs" aria-label="Kunden-Detail">{detailTabs.map((item) => <button type="button" key={item.id} className={tab === item.id ? 'is-active' : ''} onClick={() => setTab(item.id)}>{item.label}</button>)}</nav>

              <div className="customers-tab">
                {tab === 'overview' ? (
                  <>
                    <div className="customers-info-grid">
                      <article><Phone size={17} /><span>Telefon</span><strong>{detail.phone || 'Nicht hinterlegt'}</strong></article>
                      <article><Mail size={17} /><span>E-Mail</span><strong>{detail.email || 'Nicht hinterlegt'}</strong></article>
                      <article><MapPin size={17} /><span>Ort</span><strong>{detail.city || 'Nicht hinterlegt'}</strong></article>
                      <article><UserRound size={17} /><span>Aktive Ansprechpartner</span><strong>{detail.contacts.filter((contact) => contact.active).length}</strong></article>
                    </div>
                    <div className="customers-kpis">
                      <CustomizableMetrics
                        pageKey="customer-akte-overview"
                        tiles={[
                          { key: 'revenue', node: <article><TrendingUp size={16} /><span>Umsatz</span><strong>{financials ? euro(financials.revenue) : '…'}</strong><small>gebucht, netto</small></article> },
                          { key: 'opos', node: <article className={financials && financials.openReceivables > 0 ? 'is-warn' : ''}><Receipt size={16} /><span>Offene Posten</span><strong>{financials ? euro(financials.openReceivables) : '…'}</strong><small>OPOS</small></article> },
                          { key: 'machines', node: <article><Forklift size={16} /><span>Maschinen</span><strong>{assignedMachines.length}</strong><small>zugeordnet</small></article> },
                          { key: 'operations', node: <article><ClipboardList size={16} /><span>Vorgänge</span><strong>{operations.length}</strong><small>gesamt</small></article> },
                        ]}
                      />
                    </div>

                    <section className="customers-section">
                      <div className="customers-section__heading"><div><span className="customers-eyebrow">Maschinenpark</span><h3>Zugeordnete Maschinen</h3></div></div>
                      <div className="customers-machines">
                        {assignedMachines.map((machine) => (
                          <button type="button" key={machine.id} className="customers-machine-row" onClick={() => navigate(`/maschinen/${machine.number}`)}>
                            <span className="customers-machine-row__icon"><Forklift size={18} /></span>
                            <span className="customers-machine-row__body"><strong>{machine.number} · {machine.manufacturer} {machine.model}</strong><small>{machine.typeName}{machine.serialNumber ? ` · SN ${machine.serialNumber}` : ''}</small></span>
                            <span className="customers-machine-row__status">{machineStatusLabel[machine.status] ?? machine.status}</span>
                          </button>
                        ))}
                        {assignedMachines.length === 0 ? <p>Diesem Kunden sind keine Maschinen zugeordnet.</p> : null}
                      </div>
                    </section>
                  </>
                ) : null}

                {tab === 'vorgaenge' ? (
                  operations.length === 0
                    ? <p className="customers-muted">Für diesen Kunden sind noch keine Vorgänge (Auftrag, Service, Miete, Angebot) erfasst.</p>
                    : <ul className="customers-ops">
                        {operations.map((op, index) => (
                          <li key={`${op.kind}:${op.number}:${index}`}>
                            <span className={`customers-ops__kind is-${op.kind}`}>{operationKindLabel[op.kind]}</span>
                            <div><strong>{op.number || '—'}</strong><small>{op.title}</small></div>
                            <span className="customers-ops__status">{op.status.replace(/[_-]/g, ' ')}</span>
                            <time>{dateShort(op.date)}</time>
                          </li>
                        ))}
                      </ul>
                ) : null}

                {tab === 'rechnungen' ? (
                  <>
                    <div className="customers-kpis">
                      <CustomizableMetrics
                        pageKey="customer-akte-rechnungen"
                        tiles={[
                          { key: 'revenue', node: <article><TrendingUp size={16} /><span>Umsatz</span><strong>{financials ? euro(financials.revenue) : '…'}</strong><small>gebucht, netto</small></article> },
                          { key: 'opos', node: <article className={financials && financials.openReceivables > 0 ? 'is-warn' : ''}><Receipt size={16} /><span>Offene Posten</span><strong>{financials ? euro(financials.openReceivables) : '…'}</strong><small>OPOS</small></article> },
                        ]}
                      />
                    </div>
                    {!financials || financials.invoices.length === 0
                      ? <p className="customers-muted">Für diesen Kunden liegen keine Rechnungen vor.</p>
                      : <table className="customers-invoices">
                          <thead><tr><th>Beleg</th><th>Datum</th><th className="is-num">Brutto</th><th className="is-num">Offen</th><th>Status</th></tr></thead>
                          <tbody>
                            {financials.invoices.map((invoice) => (
                              <tr key={invoice.number}>
                                <td>{invoice.number}</td>
                                <td>{dateShort(invoice.date)}</td>
                                <td className="is-num">{euro(invoice.gross)}</td>
                                <td className={`is-num ${invoice.open > 0 ? 'is-open' : ''}`}>{invoice.open > 0 ? euro(invoice.open) : '–'}</td>
                                <td><span className={`customers-paystatus is-${invoice.status}`}>{paymentStatusLabel[invoice.status] ?? invoice.status}</span></td>
                              </tr>
                            ))}
                          </tbody>
                        </table>}
                  </>
                ) : null}

                {tab === 'konditionen' ? (
                  <section className="customers-section">
                    <div className="customers-section__heading"><div><span className="customers-eyebrow">Konditionen</span><h3>Konditionen & Buchhaltung</h3></div><button type="button" className="customers-secondary" disabled={!canEdit} onClick={() => setAgreementsDialog(true)}><Pencil size={15} /> Bearbeiten</button></div>
                    <div className="customers-info-grid">
                      <article><Hash size={17} /><span>Debitorennummer</span><strong>{detail.agreements.debtorNumber || '–'}</strong></article>
                      <article><Hash size={17} /><span>Leitweg-ID</span><strong>{detail.agreements.leitwegId || '–'}</strong></article>
                      <article><Percent size={17} /><span>Rabatt</span><strong>{detail.agreements.discountPercent > 0 ? `${detail.agreements.discountPercent} %` : '–'}</strong></article>
                      <article><CalendarClock size={17} /><span>Zahlungsziel</span><strong>{detail.agreements.paymentTermDays} Tage</strong></article>
                      <article><Coins size={17} /><span>Kreditlimit</span><strong>{detail.agreements.creditLimit > 0 ? euro(detail.agreements.creditLimit) : '–'}</strong></article>
                      <article className="is-wide"><Building2 size={17} /><span>Bankverbindung</span><strong>{[detail.agreements.bankName, detail.agreements.iban, detail.agreements.bic].filter(Boolean).join(' · ') || '–'}</strong></article>
                      <article className="is-wide"><FileText size={17} /><span>Konditionen / Nachlässe</span><strong>{detail.agreements.conditions || 'Keine besonderen Konditionen'}</strong></article>
                    </div>
                    {detail.agreements.specialParts.length > 0 ? (
                      <div className="customers-parts-list">
                        <span className="customers-eyebrow">Sonder-Ersatzteile</span>
                        {detail.agreements.specialParts.map((part) => (
                          <article key={part.id}><strong>{part.partNumber || '—'}</strong><span>{part.description}</span>{part.note ? <small>{part.note}</small> : null}</article>
                        ))}
                      </div>
                    ) : null}
                  </section>
                ) : null}

                {tab === 'adressen' ? (
                  <>
                    <section className="customers-section">
                      <div className="customers-section__heading"><div><span className="customers-eyebrow">Anschriften</span><h3>Adressen</h3></div><button type="button" className="customers-secondary" disabled={!canManageAddresses} onClick={() => setAddressDialog('new')}><Plus size={15} /> Anschrift</button></div>
                      <div className="customers-card-grid">
                        {detail.addresses.map((address) => (
                          <article key={address.id}>
                            <div className="customers-card__headline"><div><strong>{address.label}</strong><span>{addressKindLabel(address.kind)}</span></div>{address.primary ? <em>Hauptanschrift</em> : null}</div>
                            <p>{address.street || 'Straße fehlt'}<br />{address.postalCode} {address.city}<br />{address.countryCode}</p>
                            <div className="customers-card__actions"><button type="button" disabled={!canManageAddresses} onClick={() => setAddressDialog(address)}><Pencil size={14} /> Bearbeiten</button><button type="button" disabled={!canManageAddresses || mutationBusy} onClick={() => void deleteAddress(address)}><Trash2 size={14} /> Löschen</button></div>
                          </article>
                        ))}
                        {detail.addresses.length === 0 ? <p>Keine Anschrift hinterlegt.</p> : null}
                      </div>
                    </section>

                    <section className="customers-section">
                      <div className="customers-section__heading"><div><span className="customers-eyebrow">Kontakte</span><h3>Ansprechpartner</h3></div><button type="button" className="customers-secondary" disabled={!canManageContacts} onClick={() => setContactDialog('new')}><Plus size={15} /> Ansprechpartner</button></div>
                      <div className="customers-card-grid">
                        {detail.contacts.map((contact) => (
                          <article key={contact.id} className={!contact.active ? 'is-inactive' : ''}>
                            <div className="customers-card__headline"><div><strong>{[contact.salutation, contact.firstName, contact.lastName].filter(Boolean).join(' ')}</strong><span>{contact.position || 'Keine Funktion'}</span></div>{contact.primary && contact.active ? <em>Hauptkontakt</em> : null}</div>
                            <p>{contact.email || 'Keine E-Mail'}<br />{contact.phone || contact.mobile || 'Keine Telefonnummer'}</p>
                            <div className="customers-card__actions"><button type="button" disabled={!canManageContacts} onClick={() => setContactDialog(contact)}><Pencil size={14} /> Bearbeiten</button>{contact.active ? <button type="button" disabled={!canManageContacts || mutationBusy} onClick={() => void deactivateContact(contact)}><UserX size={14} /> Deaktivieren</button> : <span>Deaktiviert</span>}</div>
                          </article>
                        ))}
                        {detail.contacts.length === 0 ? <p>Keine Ansprechpartner hinterlegt.</p> : null}
                      </div>
                    </section>
                  </>
                ) : null}

                {tab === 'bemerkungen' ? (
                  <section className="customers-notes customers-notes--editable">
                    <div className="customers-notes__head">
                      <span>Bemerkungen</span>
                      {!notesEditing && canEdit ? <button type="button" className="customers-secondary" onClick={() => { setNotesDraft(detail.notes); setNotesEditing(true); }}><Pencil size={14} /> Bearbeiten</button> : null}
                    </div>
                    {notesEditing ? (
                      <div className="customers-notes__edit">
                        <textarea rows={5} value={notesDraft} onChange={(event) => setNotesDraft(event.target.value)} placeholder="Bemerkungen zum Kunden …" />
                        <div className="customers-notes__actions">
                          <button type="button" className="customers-secondary" onClick={() => { setNotesDraft(detail.notes); setNotesEditing(false); }} disabled={notesSaving}>Abbrechen</button>
                          <button type="button" className="customers-primary" onClick={() => void saveNotes()} disabled={notesSaving}>{notesSaving ? 'Speichert …' : 'Speichern'}</button>
                        </div>
                      </div>
                    ) : (
                      <p>{detail.notes || 'Keine Bemerkungen hinterlegt.'}</p>
                    )}
                  </section>
                ) : null}

                {tab === 'dokumente' ? (
                  <CustomerDocumentsSection tenantId={tenant.id} customerId={detail.id} gateway={gateway} canEdit={canEdit} />
                ) : null}

                {tab === 'historie' ? (
                  <dl className="customers-history">
                    <div><dt>Im Bestand seit</dt><dd>{dateShort(detail.activeSince)}</dd></div>
                    {detail.activeUntil ? <div><dt>Abgang</dt><dd>{dateShort(detail.activeUntil)}</dd></div> : null}
                    <div><dt>Angelegt</dt><dd>{new Date(detail.createdAt).toLocaleString('de-DE')}</dd></div>
                    <div><dt>Zuletzt geändert</dt><dd>{new Date(detail.updatedAt).toLocaleString('de-DE')}</dd></div>
                  </dl>
                ) : null}
              </div>
            </>
          ) : null}
        </main>
      </div>
      ) : (
        <CustomerTable customers={page.items} selectedId={selectedId} loading={loading} onOpen={(customer) => { setViewMode('split'); navigate(`/kunden/${customer.number}`); }} onContext={(event, customer) => openCustomerMenu(event, customerMenuItems(customer))} />
      )}

      {customerDialog ? <CustomerDialog key={`${customerDialog}:${detail?.id ?? 'new'}`} mode={customerDialog} tenantId={tenant.id} gateway={gateway} customer={customerDialog === 'edit' ? detail ?? undefined : undefined} duplicateCheckEnabled={duplicateCheckEnabled} onClose={() => setCustomerDialog(null)} onSaved={(saved) => applySavedCustomer(saved, customerDialog === 'create')} onChooseCandidate={(customerId) => { setCustomerDialog(null); setSelectedId(customerId); }} /> : null}
      {addressDialog && detail ? <AddressDialog key={addressDialog === 'new' ? 'new-address' : addressDialog.id} tenantId={tenant.id} customerId={detail.id} gateway={gateway} address={addressDialog === 'new' ? undefined : addressDialog} onClose={() => setAddressDialog(null)} onSaved={applySavedCustomer} onError={setError} /> : null}
      {contactDialog && detail ? <ContactDialog key={contactDialog === 'new' ? 'new-contact' : contactDialog.id} tenantId={tenant.id} customerId={detail.id} gateway={gateway} contact={contactDialog === 'new' ? undefined : contactDialog} onClose={() => setContactDialog(null)} onSaved={applySavedCustomer} onError={setError} /> : null}
      {agreementsDialog && detail ? <AgreementsDialog key={`agreements-${detail.id}`} tenantId={tenant.id} customer={detail} gateway={gateway} onClose={() => setAgreementsDialog(false)} onSaved={(saved) => { applySavedCustomer(saved); setAgreementsDialog(false); }} onError={setError} /> : null}
      {sheetOpen && detail ? <CustomerSheetModal detail={detail} machines={assignedMachines} financials={financials} operationCount={operations.length} company={company} onClose={() => setSheetOpen(false)} /> : null}
      {customerContextMenu}
      {customerDeleteDialog}
    </div>
  );
}

function CustomerSheetModal({ detail, machines, financials, operationCount, company, onClose }: {
  readonly detail: CustomerDetailRecord;
  readonly machines: readonly MachineListRecord[];
  readonly financials: CustomerFinancials | null;
  readonly operationCount: number;
  readonly company: TenantCompanyProfile;
  readonly onClose: () => void;
}) {
  const html = useMemo(() => renderCustomerSheetHtml({
    company,
    todayIso: new Date().toISOString().slice(0, 10),
    customer: {
      number: detail.number,
      displayName: detail.displayName,
      kindLabel: detail.kind === 'company' ? 'Unternehmen' : 'Privatkunde',
      statusLabel: statusLabel(detail.status),
      vatId: detail.vatId,
      taxNumber: detail.taxNumber,
      website: detail.website,
      phone: detail.phone,
      email: detail.email,
      activeSince: detail.activeSince,
      notes: detail.notes,
    },
    agreements: {
      debtorNumber: detail.agreements.debtorNumber,
      leitwegId: detail.agreements.leitwegId,
      paymentTermDays: detail.agreements.paymentTermDays,
      discountPercent: detail.agreements.discountPercent,
      creditLimit: detail.agreements.creditLimit,
      bankName: detail.agreements.bankName,
      iban: detail.agreements.iban,
      bic: detail.agreements.bic,
      conditions: detail.agreements.conditions,
    },
    addresses: detail.addresses.map((address) => ({
      label: address.label,
      kindLabel: addressKindLabel(address.kind),
      street: address.street,
      postalCode: address.postalCode,
      city: address.city,
      country: address.countryCode,
      primary: address.primary,
    })),
    contacts: detail.contacts.filter((contact) => contact.active).map((contact) => ({
      name: [contact.salutation, contact.firstName, contact.lastName].filter(Boolean).join(' '),
      position: contact.position,
      email: contact.email,
      phone: contact.phone || contact.mobile,
      primary: contact.primary,
    })),
    machines: machines.map((machine) => ({
      number: machine.number,
      label: `${machine.manufacturer} ${machine.model}`.trim(),
      statusLabel: machineStatusLabel[machine.status] ?? machine.status,
    })),
    kpis: {
      revenue: financials?.revenue ?? 0,
      openReceivables: financials?.openReceivables ?? 0,
      machineCount: machines.length,
      operationCount,
    },
  }), [detail, machines, financials, operationCount, company]);
  return (
    <div className="customers-doc-modal" role="dialog" aria-modal="true" aria-label="Kundenblatt" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}>
      <div className="customers-doc-modal__panel">
        <header><span>Kundenblatt · {detail.displayName}</span><button type="button" className="customers-secondary" onClick={onClose}>Schließen</button></header>
        <iframe className="customers-doc-frame" title="Kundenblatt" srcDoc={html} />
      </div>
    </div>
  );
}

function CustomerTable({ customers, selectedId, loading, onOpen, onContext }: {
  readonly customers: readonly CustomerListRecord[];
  readonly selectedId: string | null;
  readonly loading: boolean;
  readonly onOpen: (customer: CustomerListRecord) => void;
  readonly onContext?: (event: ReactMouseEvent, customer: CustomerListRecord) => void;
}) {
  if (!loading && customers.length === 0) return <p className="customers-muted">Keine Kunden gefunden.</p>;
  return (
    <div className="customers-table-wrap">
      <table className="customers-table">
        <thead><tr><th>Nr.</th><th>Name</th><th>Ort</th><th>Telefon</th><th>E-Mail</th><th>Kontakte</th><th>Kunde seit</th><th>Status</th></tr></thead>
        <tbody>
          {customers.map((customer) => (
            <tr key={customer.id} className={customer.id === selectedId ? 'is-active' : ''} onClick={() => onOpen(customer)} onContextMenu={onContext ? (event) => onContext(event, customer) : undefined}>
              <td className="customers-table__nr">{customer.number}</td>
              <td>{customer.displayName}</td>
              <td>{customer.city || '—'}</td>
              <td>{customer.phone || '—'}</td>
              <td>{customer.email || '—'}</td>
              <td>{customer.contactCount}</td>
              <td>{dateShort(customer.activeSince)}</td>
              <td><span className={`customers-status is-${customer.status}`}>{statusLabel(customer.status)}</span></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
